import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import * as SelectPrimitive from '@radix-ui/react-select';
import { MoreVertical, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import apiClient from '@/services/api-client';
import { apiConfig } from '@/config/api';
import { ConfirmDeleteModal } from '@/components/ConfirmDeleteModal';
import { useAuth } from '@/contexts/AuthContext';

interface InvitedUser {
  invitation_id?: string;
  id?: string;
  user_id?: string;
  email: string;
  role?: string;
  roles?: Array<{ id: string; name: string; description?: string }>;
  invitedAt?: string;
  created_at?: string;
  status?: 'Active' | 'Pending' | 'Inactive';
  is_active?: boolean;
  created_by?: string; // ID of user who created the invitation
}

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Delete confirmation modal state
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    userId: string | null;
    userEmail: string | null;
  }>({
    isOpen: false,
    userId: null,
    userEmail: null,
  });
  
  // Activate/Deactivate confirmation modal state
  const [activateModalState, setActivateModalState] = useState<{
    isOpen: boolean;
    userId: string | null;
    userEmail: string | null;
    isActive: boolean;
  }>({
    isOpen: false,
    userId: null,
    userEmail: null,
    isActive: false,
  });
  
  // Reset Password confirmation modal state
  const [resetPasswordModalState, setResetPasswordModalState] = useState<{
    isOpen: boolean;
    userId: string | null;
    userEmail: string | null;
  }>({
    isOpen: false,
    userId: null,
    userEmail: null,
  });
  
  // Dropdown menu state for actions
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  // Invited users data
  const [invitedUsers, setInvitedUsers] = useState<InvitedUser[]>([]);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);

  // Check user roles from token
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);

  // Load user info and invitations
  // Close menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.action-menu-container') && !target.closest('.fixed.inset-0')) {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [openMenuId]);

  useEffect(() => {
    const loadUserInfo = async () => {
      setIsLoadingRoles(true);
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const decoded = jwtDecode(token);
          
          const roles: string[] = [];
          if ((decoded as any).is_super_admin) roles.push('superadmin');
          if ((decoded as any).is_admin) roles.push('admin');
          if (roles.length === 0) roles.push('user');
          setUserRoles(roles);
          
          console.log('[ADMIN PAGE] Token decoded:', decoded);
          console.log('[ADMIN PAGE] User roles from token:', roles);
          console.log('[ADMIN PAGE] Has admin/superadmin:', roles.includes('admin') || roles.includes('superadmin'));
          
          // Also try to get user info from /api/v1/auth/me endpoint
          try {
            const meResponse = await apiClient.get(apiConfig.endpoints.auth.me);
            console.log('[ADMIN PAGE] User info from /me endpoint:', meResponse.data);
            // The /me endpoint might have more accurate role information
            if (meResponse.data?.roles) {
              const serverRoles = meResponse.data.roles.map((r: any) => r.name);
              setUserRoles(serverRoles);
              console.log('[ADMIN PAGE] Server says roles:', serverRoles);
            }
          } catch (meError) {
            console.warn('[ADMIN PAGE] Failed to get user info from /me endpoint:', meError);
          }
        } catch (error) {
          console.error('[ADMIN PAGE] Failed to decode token:', error);
        } finally {
          setIsLoadingRoles(false);
        }
      } else {
        setIsLoadingRoles(false);
      }
    };
    
    loadUserInfo();
  }, []);

  // Load invitations when userRoles are available
  useEffect(() => {
    if (userRoles.length > 0 && (userRoles.includes('admin') || userRoles.includes('superadmin'))) {
      loadInvitations();
    }
  }, [userRoles]);

  const loadInvitations = async () => {
    if (!userRoles.includes('admin') && !userRoles.includes('superadmin')) {
      return;
    }

    setIsLoadingInvitations(true);
    try {
      const response = await apiClient.get(apiConfig.endpoints.users.getAllInvitations);
      const invitations = response.data || [];
      
      // Map backend response to frontend format
      // Backend now filters invitations: admins see only their own, superadmins see all
      const mappedInvitations: InvitedUser[] = invitations
        .map((inv: any) => {
        // Handle roles - can be array of objects with name property, or array of strings, or single role object
        let rolesArray: Array<{ name: string }> = [];
        let roleString = 'N/A';
        
        if (inv.roles) {
          if (Array.isArray(inv.roles)) {
            rolesArray = inv.roles.map((r: any) => 
              typeof r === 'string' ? { name: r } : (r.name ? { name: r.name } : r)
            );
            roleString = rolesArray.map(r => r.name).join(', ');
          } else if (typeof inv.roles === 'object' && inv.roles.name) {
            rolesArray = [{ name: inv.roles.name }];
            roleString = inv.roles.name;
          }
        } else if (inv.role) {
          const roleName = typeof inv.role === 'string' ? inv.role : inv.role.name;
          rolesArray = [{ name: roleName }];
          roleString = roleName;
        }
        
          return {
            invitation_id: inv.invitation_id || inv.id,
            user_id: inv.user_id,
            email: inv.email,
            roles: rolesArray,
            role: roleString,
            created_at: inv.created_at,
            invitedAt: inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '',
            is_active: inv.is_active,
            status: inv.is_active ? 'Active' : (inv.user_id ? 'Pending' : 'Inactive'),
            created_by: inv.created_by || inv.invited_by || null,
          };
        });
      
      setInvitedUsers(mappedInvitations);
    } catch (err: any) {
      console.error('Error loading invitations:', err);
      setError(err.response?.data?.detail || 'Failed to load invitations');
    } finally {
      setIsLoadingInvitations(false);
    }
  };

  const handleInvite = async () => {
    if (!email || !selectedRole) {
      setError('Please fill in both email and role');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use relative URL with proxy (proxy should handle it correctly)
      // If proxy doesn't work, we can try direct URL later
      const requestData = {
        email: email,
        roles: [selectedRole],
      };
      
      const response = await apiClient.post(
        apiConfig.endpoints.users.invitations,
        requestData
      );

      console.log('Invitation sent successfully:', response.data);

      // Reset form on success
      setEmail('');
      setSelectedRole('');
      setError(null);
      
      // Reload invitations list
      await loadInvitations();
    } catch (err: any) {
      console.error('Error inviting user:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        headers: err.response?.headers,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          headers: err.config?.headers,
        },
      });

      // Detailed error handling
      if (err.response?.status === 405) {
        setError(
          'Method Not Allowed (405). Please check: 1) You are logged in with a user that has "admin" or "superadmin" role, 2) Your token is valid, 3) Try logging out and back in. If the problem persists, contact support.'
        );
      } else if (err.response?.status === 401) {
        setError(
          'Unauthorized (401). Your session may have expired. Please logout and login again.'
        );
      } else if (err.response?.status === 403) {
        const detail = err.response?.data?.detail || 'Unknown error';
        setError(
          `Forbidden (403). ${detail}. You need "admin" or "superadmin" role to invite users. Please contact your administrator if you believe you should have access.`
        );
      } else {
        const errorMessage =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.response?.data?.detail ||
          'Failed to send invitation. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (userId: string, userEmail: string) => {
    setDeleteModalState({
      isOpen: true,
      userId,
      userEmail,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModalState({
      isOpen: false,
      userId: null,
      userEmail: null,
    });
  };

  const handleDeleteUser = async () => {
    if (!deleteModalState.userId) return;

    const userId = deleteModalState.userId;

    try {
      console.log('[DELETE] Attempting to delete user:', userId);
      console.log('[DELETE] Endpoint:', apiConfig.endpoints.users.delete(userId));
      
      const response = await apiClient.delete(apiConfig.endpoints.users.delete(userId));
      
      console.log('[DELETE] User deleted successfully:', response.data);
      setError(null);
      closeDeleteModal();
      await loadInvitations();
    } catch (err: any) {
      console.error('[DELETE] Error deleting user:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        userId: userId,
        endpoint: apiConfig.endpoints.users.delete(userId),
      });
      
      if (err.response?.status === 400) {
        setError(err.response?.data?.detail || 'Cannot delete this user');
      } else if (err.response?.status === 404) {
        setError('User not found');
      } else if (err.response?.status === 401) {
        setError('Unauthorized. Please logout and login again.');
      } else if (err.response?.status === 403) {
        setError('Forbidden. You need admin or superadmin role to delete users.');
      } else {
        setError(err.response?.data?.detail || 'Failed to delete user. Please try again.');
      }
      closeDeleteModal();
    }
  };

  const openActivateModal = (userId: string, userEmail: string, isActive: boolean) => {
    setActivateModalState({
      isOpen: true,
      userId,
      userEmail,
      isActive,
    });
  };

  const closeActivateModal = () => {
    setActivateModalState({
      isOpen: false,
      userId: null,
      userEmail: null,
      isActive: false,
    });
  };

  const handleActivateUser = async () => {
    if (!activateModalState.userId) return;

    const userId = activateModalState.userId;
    const isActive = activateModalState.isActive;

    try {
      // FastAPI treats bool parameters as query params by default
      await apiClient.patch(
        `${apiConfig.endpoints.users.activate(userId)}?is_active=${isActive}`,
        {}
      );
      setError(null);
      await loadInvitations();
      closeActivateModal();
    } catch (err: any) {
      console.error('Error updating user status:', err);
      setError(err.response?.data?.detail || 'Failed to update user status');
      closeActivateModal();
    }
  };

  const openResetPasswordModal = (userId: string, userEmail: string) => {
    setResetPasswordModalState({
      isOpen: true,
      userId,
      userEmail,
    });
  };

  const closeResetPasswordModal = () => {
    setResetPasswordModalState({
      isOpen: false,
      userId: null,
      userEmail: null,
    });
  };

  const handleResetPassword = async () => {
    if (!resetPasswordModalState.userId) return;

    const userId = resetPasswordModalState.userId;

    try {
      await apiClient.post(apiConfig.endpoints.users.resetPassword(userId));
      setError(null);
      await loadInvitations();
      closeResetPasswordModal();
    } catch (err: any) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.detail || 'Failed to reset password');
      closeResetPasswordModal();
    }
  };

  const handleResendInvitation = async (userId: string) => {
    try {
      await apiClient.post(apiConfig.endpoints.users.resendInvitation(userId));
      setError(null);
      alert('Invitation resent successfully!');
      await loadInvitations();
    } catch (err: any) {
      console.error('Error resending invitation:', err);
      setError(err.response?.data?.detail || 'Failed to resend invitation');
    }
  };


  // Check if user is only "user" role (not admin/superadmin)
  const isRegularUser = !isLoadingRoles && userRoles.length > 0 && 
    !userRoles.includes('admin') && 
    !userRoles.includes('superadmin') &&
    (userRoles.includes('user') || userRoles.length === 0);

  // Get display name for user
  const getDisplayName = () => {
    if (user?.first_name) return user.first_name;
    // Always use part of email before @, never full email
    if (user?.email) return user.email.split('@')[0];
    // Fallback to username if no email (shouldn't happen normally)
    if (user?.username) return user.username;
    return 'User';
  };

  // Show loading while roles are being checked
  if (isLoadingRoles) {
    return (
      <div className="page-container" data-test-id="admin-page-container">
        <div className="page-inner" data-test-id="admin-page-inner">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-gray-600">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  // If user is only "user" role, show access denied message
  if (isRegularUser) {
    return (
      <div className="page-container" data-test-id="admin-page-container">
        <div className="page-inner" data-test-id="admin-page-inner">
          <Card className="w-full max-w-2xl mx-auto mt-8">
            <CardContent className="p-8 text-center">
              <h1 className="text-3xl font-bold mb-4 text-gray-900">Access Denied</h1>
              <p className="text-lg text-gray-600 mb-6">
                You don't have permission to view this page.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                This page is only accessible to administrators.
              </p>
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                className="mt-4"
                data-test-id="go-to-profile-button"
              >
                Go to Profile
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Admin/Superadmin view - show full admin panel
  return (
    <div className="page-container" data-test-id="admin-page-container">
      <div className="page-inner" data-test-id="admin-page-inner">
        {/* Welcome Section */}
        <div className="mb-8" data-test-id="welcome-section">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl font-bold mb-2 text-gray-900">Hello, {getDisplayName()}!</h1>
              <p className="text-lg text-gray-600">
                Your role is:{' '}
                <span className="text-blue-600 font-semibold">
                  {userRoles.length > 0 ? userRoles.join(', ') : 'Unknown'}
                </span>
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                className="flex items-center gap-2"
                data-test-id="profile-settings-button"
              >
                <Settings className="h-4 w-4" />
                Profile Settings
              </Button>
            </div>
          </div>
        </div>

        {/* Invite Users Section */}
        <Card className="mb-8 p-6" data-test-id="invite-users-card">
          <h2 className="text-2xl font-bold mb-6 text-gray-900">Invite Users</h2>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label
                htmlFor="email-input"
                className="block text-sm font-medium mb-2 text-gray-700"
              >
                Enter email address
              </label>
              <Input
                id="email-input"
                type="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
                disabled={isLoading}
                data-test-id="invite-email-input"
              />
            </div>
            <div className="flex-1">
              <label
                htmlFor="role-select"
                className="block text-sm font-medium mb-2 text-gray-700"
              >
                Select a Role
              </label>
              <Select
                value={selectedRole}
                onValueChange={setSelectedRole}
                disabled={isLoading}
              >
                <SelectTrigger
                  id="role-select"
                  className="w-full"
                  data-test-id="invite-role-select"
                >
                  <SelectValue placeholder="Select a Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectPrimitive.Item
                    value="user"
                    className="select-item relative data-[state=checked]:select-item-active"
                  >
                    <SelectPrimitive.ItemText>User</SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                  <SelectPrimitive.Item
                    value="admin"
                    className="select-item relative data-[state=checked]:select-item-active"
                  >
                    <SelectPrimitive.ItemText>Admin</SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-medium mb-2 text-gray-700 opacity-0 pointer-events-none">
                Action
              </label>
              <Button
                onClick={handleInvite}
                disabled={isLoading || !email || !selectedRole}
                className="w-full md:w-auto px-6 bg-blue-500 hover:bg-blue-600 text-white h-10"
                data-test-id="invite-button"
              >
                {isLoading ? 'Sending...' : 'Invite'}
              </Button>
            </div>
          </div>
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-test-id="invite-error">
              {error}
            </div>
          )}
        </Card>

        {/* Invited Users Section */}
        <Card className="p-6" data-test-id="invited-users-card">
          <h2 className="text-2xl font-bold mb-6 text-gray-900">
            Invited Users
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    EMAIL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    ROLE
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    INVITED AT
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    STATUS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoadingInvitations ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-8 text-center text-sm text-gray-500"
                    >
                      Loading invitations...
                    </td>
                  </tr>
                ) : invitedUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-8 text-center text-sm text-gray-500"
                    >
                      No invited users yet
                    </td>
                  </tr>
                ) : (
                  invitedUsers.map((user) => {
                    const userId = user.user_id || user.id || user.invitation_id || '';
                    // Handle roles display - roles can be array of objects or strings
                    let userRoles = 'N/A';
                    if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
                      userRoles = user.roles.map((r: any) => 
                        typeof r === 'string' ? r : (r.name || r)
                      ).join(', ');
                    } else if (user.role) {
                      userRoles = typeof user.role === 'string' ? user.role : (typeof user.role === 'object' && user.role !== null && 'name' in user.role ? (user.role as any).name : String(user.role));
                    }
                    const status = user.is_active ? 'Active' : (user.user_id ? 'Pending' : 'Inactive');
                    
                    return (
                      <tr
                        key={userId}
                        className="hover:bg-gray-50"
                        data-test-id={`user-row-${userId}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {userRoles}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.invitedAt || (user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              status === 'Active'
                                ? 'bg-green-100 text-green-800'
                                : status === 'Pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="relative action-menu-container">
                            <button
                              onClick={(e) => {
                                const button = e.currentTarget;
                                const rect = button.getBoundingClientRect();
                                const menuHeight = 120; // Approximate menu height
                                const spaceBelow = window.innerHeight - rect.bottom;
                                const spaceAbove = rect.top;
                                
                                // Calculate position: prefer opening downward, but open upward if not enough space
                                let top: number;
                                if (spaceBelow >= menuHeight || spaceBelow > spaceAbove) {
                                  // Open downward
                                  top = rect.bottom + 4; // 4px gap
                                } else {
                                  // Open upward
                                  top = rect.top - menuHeight - 4; // 4px gap
                                }
                                
                                setMenuPosition({
                                  top,
                                  right: window.innerWidth - rect.right,
                                });
                                setOpenMenuId(openMenuId === userId ? null : (userId || null));
                              }}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                              data-test-id={`action-menu-${userId}`}
                            >
                              <MoreVertical className="h-5 w-5" />
                            </button>
                            
                            {openMenuId === userId && menuPosition && (
                              <>
                                {/* Backdrop to close menu when clicking outside */}
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    setMenuPosition(null);
                                  }}
                                />
                                {/* Menu positioned relative to viewport */}
                                <div
                                  className="fixed w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200"
                                  style={{
                                    top: `${menuPosition.top}px`,
                                    right: `${menuPosition.right}px`,
                                  }}
                                >
                                  <div className="py-1">
                                    {user.user_id ? (
                                      <>
                                        <button
                                          onClick={() => {
                                            openActivateModal(user.user_id!, user.email, !user.is_active);
                                            setOpenMenuId(null);
                                            setMenuPosition(null);
                                          }}
                                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                          {user.is_active ? 'Deactivate' : 'Activate'}
                                        </button>
                                        <button
                                          onClick={() => {
                                            openResetPasswordModal(user.user_id!, user.email);
                                            setOpenMenuId(null);
                                            setMenuPosition(null);
                                          }}
                                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                          Reset Password
                                        </button>
                                        <button
                                          onClick={() => {
                                            openDeleteModal(user.user_id!, user.email);
                                            setOpenMenuId(null);
                                            setMenuPosition(null);
                                          }}
                                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                        >
                                          Delete User
                                        </button>
                                      </>
                                    ) : user.invitation_id ? (
                                      <button
                                        onClick={() => {
                                          handleResendInvitation(user.invitation_id!);
                                          setOpenMenuId(null);
                                          setMenuPosition(null);
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                        Resend Invitation
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <ConfirmDeleteModal
        isOpen={deleteModalState.isOpen}
        onConfirm={handleDeleteUser}
        onCancel={closeDeleteModal}
        userEmail={deleteModalState.userEmail || undefined}
      />
      <ConfirmDeleteModal
        isOpen={activateModalState.isOpen}
        onConfirm={handleActivateUser}
        onCancel={closeActivateModal}
        userEmail={activateModalState.userEmail || undefined}
        title={activateModalState.isActive ? 'Activate User' : 'Deactivate User'}
        message={
          activateModalState.userEmail
            ? `Are you sure you want to ${activateModalState.isActive ? 'activate' : 'deactivate'} user "${activateModalState.userEmail}"?`
            : `Are you sure you want to ${activateModalState.isActive ? 'activate' : 'deactivate'} this user?`
        }
        confirmButtonVariant="default"
      />
      <ConfirmDeleteModal
        isOpen={resetPasswordModalState.isOpen}
        onConfirm={handleResetPassword}
        onCancel={closeResetPasswordModal}
        userEmail={resetPasswordModalState.userEmail || undefined}
        title="Reset Password"
        message={
          resetPasswordModalState.userEmail
            ? `Are you sure you want to reset password for user "${resetPasswordModalState.userEmail}"? A new password will be sent to their email.`
            : 'Are you sure you want to reset this user\'s password? A new password will be sent to their email.'
        }
        confirmButtonVariant="default"
        confirmButtonText="Reset Password"
      />
    </div>
  );
};

export default AdminPage;

